import { flattenPayloadFormat, unescapeDotsInKey } from '../components/title-bar/utils'
import type { JsonValue } from '../types'

export type FormValues = Record<string, string | number | boolean>

export const buildNestedPayload = (
  formValues: FormValues,
  flattenedFormat: Record<string, string>,
): Record<string, JsonValue> => {
  const payload: Record<string, JsonValue> = {}

  Object.entries(flattenedFormat).forEach(([key, fieldType]) => {
    const value = formValues[key]
    const processedValue = processValue(value, fieldType)

    if (processedValue === undefined) return

    const pathKeys = key.split('.')
    let current = payload

    pathKeys.forEach((pathKey, index) => {
      const actualKey = unescapeDotsInKey(pathKey)
      if (index === pathKeys.length - 1) {
        current[actualKey] = processedValue
      } else {
        if (!current[actualKey] || typeof current[actualKey] !== 'object') {
          current[actualKey] = {}
        }
        current = current[actualKey] as Record<string, JsonValue>
      }
    })
  })

  return payload
}

const processValue = (
  value: string | number | boolean | undefined,
  fieldType: string,
): JsonValue | undefined => {
  // If no value provided, return placeholder
  if (value === undefined || value === '') return '<fill_your_data>'

  switch (fieldType) {
    case 'boolean':
      return Boolean(value)
    case 'number': {
      if (typeof value === 'number') return value
      const numValue = Number(value)
      return Number.isNaN(numValue) ? '<fill_your_data>' : numValue
    }
    case 'list':
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) return parsed
        } catch {
          return '<fill_your_data>'
        }
      }
      return '<fill_your_data>'
    case 'json':
      if (typeof value === 'string') {
        try {
          return JSON.parse(value)
        } catch {
          return '<fill_your_data>'
        }
      }
      return '<fill_your_data>'
    default:
      return String(value)
  }
}

export type CodeLanguage = 'curl' | 'python' | 'node' | 'go' | 'ruby' | 'php' | 'java' | 'dotnet'

/**
 * Stands in for the Basic-auth credential when the auth token isn't available to this
 * session. It sits where the base64 of `authId:authToken` goes, so it spells that out rather
 * than inviting a raw token to be pasted in. Same spirit as the `<fill_your_data>` payload
 * placeholder: obviously a blank to fill in, rather than a credential that looks real.
 */
export const AUTH_TOKEN_PLACEHOLDER = '<BASE64_OF_AUTH_ID:AUTH_TOKEN>'

export const getPayloadData = (
  payloadFormat: Record<string, unknown> | string,
  formValues?: FormValues,
) => {
  let parsedFormat: Record<string, string | Record<string, unknown>> = {}
  try {
    parsedFormat =
      typeof payloadFormat === 'string' ? JSON.parse(payloadFormat || '{}') : payloadFormat || {}
  } catch {
    parsedFormat = {}
  }

  const flattenedFormat = flattenPayloadFormat(parsedFormat)
  const payload = buildNestedPayload(formValues || {}, flattenedFormat)
  return { payload, payloadStr: JSON.stringify(payload, null, 2) }
}

export const generateCode = (
  language: CodeLanguage,
  url: string,
  authId: string,
  authToken: string,
  payloadFormat: Record<string, unknown> | string,
  formValues?: FormValues,
  includeAuth = true,
): string => {
  // btoa(`${authId}:`) on a missing token produces a well-formed header that 401s on first
  // use and gives the reader nothing to correct, so emit the placeholder instead.
  const base64Auth = authToken ? btoa(`${authId}:${authToken}`) : AUTH_TOKEN_PLACEHOLDER
  const { payloadStr } = getPayloadData(payloadFormat, formValues)

  switch (language) {
    case 'curl': {
      const escapedPayload = payloadStr.replace(/'/g, "'\\''")
      const authHeader = includeAuth ? `\n--header 'Authorization: Basic ${base64Auth}' \\` : ''
      return `curl --location '${url}' \\
--header 'Content-Type: application/json' \\${authHeader}
--data '${escapedPayload}'`
    }

    case 'python': {
      const authLine = includeAuth ? `,\n    "Authorization": "Basic ${base64Auth}"` : ''
      return `import requests
import json

url = "${url}"

headers = {
    "Content-Type": "application/json"${authLine}
}

payload = json.loads('''${payloadStr.replace(/'/g, "\\'")}''')

response = requests.post(url, headers=headers, json=payload)

print(response.text)`
    }

    case 'node': {
      const authLine = includeAuth ? `,\n    'Authorization': 'Basic ${base64Auth}'` : ''
      return `const url = '${url}';
const payload = ${payloadStr};

const options = {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'${authLine}
  },
  body: JSON.stringify(payload)
};

fetch(url, options)
  .then(res => res.json())
  .then(json => console.log(json))
  .catch(err => console.error(err));`
    }

    case 'go': {
      const authLine = includeAuth
        ? `\n    req.Header.Add("Authorization", "Basic ${base64Auth}")`
        : ''
      return `package main

import (
    "bytes"
    "fmt"
    "io"
    "net/http"
)

func main() {
    url := "${url}"

    payload := []byte(\`${payloadStr}\`)

    req, err := http.NewRequest("POST", url, bytes.NewBuffer(payload))
    if err != nil {
        fmt.Println(err)
        return
    }

    req.Header.Add("Content-Type", "application/json")${authLine}

    client := &http.Client{}
    resp, err := client.Do(req)
    if err != nil {
        fmt.Println(err)
        return
    }
    defer resp.Body.Close()

    body, err := io.ReadAll(resp.Body)
    if err != nil {
        fmt.Println(err)
        return
    }

    fmt.Println(string(body))
}`
    }

    case 'ruby': {
      const authLine = includeAuth ? `\nrequest["Authorization"] = "Basic ${base64Auth}"` : ''
      return `require 'net/http'
require 'json'

url = URI("${url}")

http = Net::HTTP.new(url.host, url.port)
http.use_ssl = true

request = Net::HTTP::Post.new(url)
request["Content-Type"] = "application/json"${authLine}
request.body = '${payloadStr.replace(/'/g, "\\'")}'

response = http.request(request)
puts response.read_body`
    }

    case 'php': {
      const authLine = includeAuth ? `,\n    "Authorization: Basic ${base64Auth}"` : ''
      return `<?php

$url = "${url}";

$payload = '${payloadStr.replace(/'/g, "\\'")}';

$headers = array(
    "Content-Type: application/json"${authLine}
);

$ch = curl_init();

curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);

echo $response;

?>`
    }

    case 'java': {
      const authLine = includeAuth
        ? `\n            .header("Authorization", "Basic ${base64Auth}")`
        : ''
      return `import java.io.IOException;
import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class Main {
    public static void main(String[] args) throws IOException, InterruptedException {
        String url = "${url}";

        String payload = "${payloadStr.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}";

        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(url))
            .header("Content-Type", "application/json")${authLine}
            .POST(HttpRequest.BodyPublishers.ofString(payload))
            .build();

        HttpClient client = HttpClient.newHttpClient();
        HttpResponse<String> response = client.send(request, HttpResponse.BodyHandlers.ofString());

        System.out.println(response.body());
    }
}`
    }

    case 'dotnet': {
      const authLine = includeAuth
        ? `\n        client.DefaultRequestHeaders.Add("Authorization", "Basic ${base64Auth}");`
        : ''
      return `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;

class Program
{
    static async Task Main(string[] args)
    {
        string url = "${url}";

        string payload = @"${payloadStr.replace(/"/g, '""')}";

        using var client = new HttpClient();${authLine}

        var content = new StringContent(payload, Encoding.UTF8, "application/json");
        var response = await client.PostAsync(url, content);

        string result = await response.Content.ReadAsStringAsync();
        Console.WriteLine(result);
    }
}`
    }
  }
}
